export const condoInitialFilters = {
  search: '',
  type: '',
  location: '',
  neighborhood: '',
  purpose: '',
  minPrice: '',
  maxPrice: '',
  minUnits: '',
  maxUnits: '',
  minArea: '',
  maxArea: '',
  orderBy: '',
  displayStatus: ''
};

export const toJsDate = (value) => {
  if (value == null) return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export function getCondoNumericPrice(condo) {
  const rent = Number(condo.rent);
  if (condo.purpose === 'rent' && rent > 0) return rent;
  const priceM2 = parseFloat(String(condo.price ?? '').replace(',', '.'));
  return Number.isFinite(priceM2) ? priceM2 : 0;
}

export function getCondoLandArea(condo) {
  const n = parseFloat(String(condo.landArea ?? condo.area ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function getCondoUnits(condo) {
  return parseInt(String(condo.condominiumPropertiesCount ?? '').replace(/\D/g, ''), 10) || 0;
}

export function filterCondominiums(list, filters) {
  let result = [...list];
  const search = (filters.search || '').trim().toLowerCase();

  if (search) {
    result = result.filter((c) => {
      const haystack = [
        c.title,
        c.code,
        c.city,
        c.neighborhood,
        c.address
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }

  if (filters.type) {
    result = result.filter((c) => c.type === filters.type);
  }

  if (filters.purpose) {
    result = result.filter((c) => c.purpose === filters.purpose);
  }

  if (filters.location) {
    result = result.filter((c) => String(c.city || '').trim() === filters.location);
  }

  if (filters.neighborhood) {
    const selected = filters.neighborhood.split(',').filter(Boolean);
    if (selected.length > 0) {
      result = result.filter((c) => selected.includes(String(c.neighborhood || '').trim()));
    }
  }

  if (filters.minPrice) {
    result = result.filter((c) => getCondoNumericPrice(c) >= parseFloat(filters.minPrice));
  }

  if (filters.maxPrice) {
    result = result.filter((c) => getCondoNumericPrice(c) <= parseFloat(filters.maxPrice));
  }

  if (filters.minUnits) {
    result = result.filter((c) => getCondoUnits(c) >= parseInt(filters.minUnits, 10));
  }

  if (filters.maxUnits) {
    result = result.filter((c) => getCondoUnits(c) <= parseInt(filters.maxUnits, 10));
  }

  if (filters.minArea) {
    result = result.filter((c) => getCondoLandArea(c) >= parseFloat(filters.minArea));
  }

  if (filters.maxArea) {
    result = result.filter((c) => getCondoLandArea(c) <= parseFloat(filters.maxArea));
  }

  if (filters.displayStatus) {
    result = result.filter((c) => {
      if (filters.displayStatus === 'reserved') {
        return c.isReserved || c.displayTag === 'reserved';
      }
      if (filters.displayStatus === 'featured') {
        return c.showInHome || c.isFeatured || c.displayTag === 'featured';
      }
      return c.displayTag === filters.displayStatus;
    });
  }

  return result;
}

export function sortCondominiums(list, orderBy) {
  const sorted = [...list];
  switch (orderBy) {
    case 'price_asc':
      return sorted.sort((a, b) => getCondoNumericPrice(a) - getCondoNumericPrice(b));
    case 'price_desc':
      return sorted.sort((a, b) => getCondoNumericPrice(b) - getCondoNumericPrice(a));
    case 'newest':
      return sorted.sort(
        (a, b) => (toJsDate(b.createdAt)?.getTime() || 0) - (toJsDate(a.createdAt)?.getTime() || 0)
      );
    case 'oldest':
      return sorted.sort(
        (a, b) => (toJsDate(a.createdAt)?.getTime() || 0) - (toJsDate(b.createdAt)?.getTime() || 0)
      );
    case 'area_asc':
      return sorted.sort((a, b) => getCondoLandArea(a) - getCondoLandArea(b));
    case 'area_desc':
      return sorted.sort((a, b) => getCondoLandArea(b) - getCondoLandArea(a));
    case 'units_asc':
      return sorted.sort((a, b) => getCondoUnits(a) - getCondoUnits(b));
    case 'units_desc':
      return sorted.sort((a, b) => getCondoUnits(b) - getCondoUnits(a));
    default:
      return sorted.sort(
        (a, b) => (toJsDate(b.createdAt)?.getTime() || 0) - (toJsDate(a.createdAt)?.getTime() || 0)
      );
  }
}

export function condosToMapProperties(condos, propertyCounts = {}) {
  return condos.map((c) => ({
    ...c,
    address: c.address || [c.neighborhood, c.city].filter(Boolean).join(', ') || 'Brasil',
    price: getCondoNumericPrice(c),
    images: c.images?.length ? c.images : c.coverImage ? [c.coverImage] : [],
    coverImage: c.coverImage || c.images?.[0],
    bedrooms: getCondoUnits(c),
    bathrooms: propertyCounts[c.id] || 0,
    area: getCondoLandArea(c),
    parkingSpaces: parseInt(c.parkingSpaces, 10) || 0
  }));
}
